import { useCallback, useState } from 'react'
import { ProTable, type ProColumns } from '@ant-design/pro-components'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { Alert, Button, Modal, Tag, Typography } from 'antd'
import type { OrderGroup } from '@super-mega/server/contracts/order'
import { DEFAULT_SELECTOR_SEARCH as DEFAULT_SEARCH } from '@/const/list.ts'
import { usePageTableNavigation, type PageTableNavigate } from '@/hooks/index.ts'
import { useTRPC } from '@/trpc/react.ts'
import { getErrorMessage } from '@/utils/error.ts'
import { getPositiveIntegerIdSearchValue } from '@/utils/list-query.ts'

type OrderGroupSelectorSearch = {
  current: number
  pageSize: number
  id?: number
  keyword?: string
}

type OrderGroupSearchFormValues = Pick<OrderGroupSelectorSearch, 'keyword'> & {
  idSearch?: string
}

type OrderGroupSelectorModalProps = {
  customerId?: number
  initialSelectedGroup?: OrderGroup
  onCancel: () => void
  onSelect: (group: OrderGroup) => void
  open: boolean
}

export function OrderGroupSelectorModal(props: OrderGroupSelectorModalProps) {
  return <OrderGroupSelectorSession key={`${props.open}:${props.customerId ?? 'none'}`} {...props} />
}

function OrderGroupSelectorSession({
  customerId,
  initialSelectedGroup,
  onCancel,
  onSelect,
  open
}: OrderGroupSelectorModalProps) {
  const trpc = useTRPC()
  const [search, setSearch] = useState<OrderGroupSelectorSearch>(DEFAULT_SEARCH)
  const [selectedGroup, setSelectedGroup] = useState<OrderGroup | undefined>(() =>
    initialSelectedGroup?.customerId === customerId ? initialSelectedGroup : undefined
  )
  const groups = useQuery({
    ...trpc.order.groups.list.queryOptions({ ...search, customerId }),
    enabled: open && Boolean(customerId),
    placeholderData: keepPreviousData
  })
  const navigate = useCallback<PageTableNavigate<OrderGroupSelectorSearch>>(({ search: nextSearch }) => {
    setSearch((previous) => (typeof nextSearch === 'function' ? nextSearch(previous) : nextSearch))
  }, [])
  const table = usePageTableNavigation<OrderGroupSelectorSearch, OrderGroupSearchFormValues>({
    defaultSearch: DEFAULT_SEARCH,
    getFormInitialValues: (currentSearch) => ({
      idSearch: currentSearch.id === undefined ? undefined : String(currentSearch.id),
      keyword: currentSearch.keyword
    }),
    getSearchFromForm: (values) => ({
      id: getPositiveIntegerIdSearchValue(values.idSearch),
      keyword: values.keyword?.trim() || undefined
    }),
    navigate,
    search,
    showTotal: (total) => `共 ${total} 个订单组`
  })

  const columns: ProColumns<OrderGroup>[] = [
    {
      dataIndex: 'idSearch',
      fieldProps: { allowClear: true, inputMode: 'numeric', placeholder: '输入订单组 ID' },
      hideInTable: true,
      title: 'ID'
    },
    {
      dataIndex: 'keyword',
      fieldProps: { allowClear: true, placeholder: '订单组名称或 ID' },
      hideInTable: true,
      title: '关键词'
    },
    {
      copyable: true,
      dataIndex: 'id',
      renderText: (value?: number) => (value === undefined ? '—' : String(value)),
      search: false,
      title: 'ID',
      width: '7rem'
    },
    {
      dataIndex: 'name',
      ellipsis: true,
      render: (_, group) => (
        <Typography.Text className="block max-w-full truncate" strong>
          {group.name}
        </Typography.Text>
      ),
      search: false,
      title: '订单组名称',
      width: '18rem'
    },
    {
      dataIndex: 'customerName',
      ellipsis: true,
      renderText: (value: string) => value || '—',
      search: false,
      title: '客户',
      width: '16rem'
    },
    {
      dataIndex: 'orderCount',
      render: (_, group) => (
        <div className="flex flex-wrap gap-1">
          <Tag>{group.orderCount} 个订单</Tag>
          {group.serviceOrderCount > 0 ? <Tag color="blue">服务 {group.serviceOrderCount}</Tag> : null}
          {group.rentalOrderCount > 0 ? <Tag color="green">租赁 {group.rentalOrderCount}</Tag> : null}
        </div>
      ),
      search: false,
      title: '订单数量',
      width: '9rem'
    },
    {
      dataIndex: 'updatedAt',
      search: false,
      title: '最近更新',
      valueType: 'dateTime',
      width: '12rem'
    }
  ]

  return (
    <Modal
      cancelText="取消"
      classNames={{ container: 'h-[80vh]' }}
      destroyOnHidden
      mask={{ closable: false }}
      okButtonProps={{ disabled: !selectedGroup }}
      okText="确定"
      onCancel={onCancel}
      onOk={() => {
        if (selectedGroup) {
          onSelect(selectedGroup)
        }
      }}
      open={open}
      title="选择已有订单组"
      width="60rem"
    >
      {!customerId ? (
        <Alert showIcon title="请先选择客户，再选择订单组" type="info" />
      ) : groups.isError ? (
        <Alert
          action={
            <Button onClick={() => void groups.refetch()} size="small">
              重试
            </Button>
          }
          showIcon
          title={getErrorMessage(groups.error, '订单组列表加载失败')}
          type="error"
        />
      ) : (
        <ProTable<OrderGroup, OrderGroupSelectorSearch>
          columns={columns}
          columnsState={{ persistenceKey: 'orders-groups-selector:table', persistenceType: 'localStorage' }}
          dataSource={groups.data?.data ?? []}
          loading={{ delay: 150, spinning: groups.isFetching }}
          onReset={table.onReset}
          onSubmit={table.onSubmit}
          toolBarRender={false}
          pagination={{ ...table.pagination, total: groups.data?.total ?? 0 }}
          formRef={table.formRef}
          rowKey="id"
          rowSelection={{
            onChange: (_, selectedRows) => setSelectedGroup(selectedRows[0]),
            selectedRowKeys: selectedGroup ? [selectedGroup.id] : [],
            type: 'radio'
          }}
          scroll={{ x: '65rem' }}
          form={{ initialValues: table.formInitialValues }}
          search={{ defaultCollapsed: false, labelWidth: 'auto' }}
          size="small"
        />
      )}
    </Modal>
  )
}
